'use client';
import { currentAccountAtom } from '@/app/states/atoms';
import { useAtomValue } from 'jotai';
import { ReactNode } from 'react';

/**
 * ProtectFeatureFlag component that gates access to content based on feature flags assigned to the user.
 * Only renders children if the user has the required feature flags assigned and enabled.
 *
 * @example
 * // Basic usage - content will not render if user doesn't have the feature flag
 * <ProtectFeatureFlag flags={['my-feature-key']}>
 *   <FeatureContent />
 * </ProtectFeatureFlag>
 *
 * @example
 * // With fallback content for users without the feature flag
 * <ProtectFeatureFlag
 *   flags={['my-feature-key']}
 *   fallback={<p>This feature is not available to you</p>}
 * >
 *   <div>
 *     <h2>Feature Content</h2>
 *     <FeatureControls />
 *   </div>
 * </ProtectFeatureFlag>
 *
 * @example
 * // Multiple feature flags - user needs ALL of them
 * <ProtectFeatureFlag flags={['feature-1', 'feature-2']}>
 *   <CombinedFeatureContent />
 * </ProtectFeatureFlag>
 */
export interface ProtectFeatureFlagProps {
  /** Array of feature flag keys that are required. User must have ALL of them assigned and enabled. */
  flags: string[];
  /** Optional content to display when access is denied. If not provided, renders null */
  fallback?: ReactNode;
  /** The content to render when access is granted */
  children: ReactNode;
}

export function ProtectFeatureFlag({ flags, fallback, children }: ProtectFeatureFlagProps) {
  const currentAccount = useAtomValue(currentAccountAtom);
  const userFlags = currentAccount?.flags || [];

  // Check if user has all required feature flags
  // Flags are expected to be an array of flag keys (strings)
  const hasAllFlags = flags.every(flagKey => userFlags.includes(flagKey));

  if (hasAllFlags) {
    return <>{children}</>;
  }

  return fallback ? <>{fallback}</> : null;
}
